import { LoadingSpinner, PageFooter } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { ImageCropperModal } from "../../IndependantContractorBadge/BottomSheet/ImageCropperModal";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { CameraPermissionBottomSheet } from "../../WorkerShifts/Timekeeping/TimeclockComplianceVideoProof/dialogs/CameraPermissionBottomSheet";
import { useWorkBadgeSelectHeadshot } from "../hooks/useWorkBadgeSelectHeadshot";
import { BadgePhotoSelectionBottomSheet } from "./BadgePhotoSelectionBottomSheet";
import { WorkBadgePreviewContainer } from "./WorkBadgePreviewContainer";

interface WorkBadgeSelectHeadshotProps {
  qualificationName: string;
  onConfirm: (workerHeadshotBase64: string) => void;
  onBack: () => void;
}

export function WorkBadgeSelectHeadshot(props: WorkBadgeSelectHeadshotProps) {
  const { qualificationName, onConfirm, onBack } = props;

  const {
    badgePhotoSelectionModalState,
    isPicturesLoading,
    workerHeadshotBase64,
    hasProfilePicture,
    handleUseProfilePictureForBadge,
    handleTakePhoto,
    handleSelectPhotoFromCameraRoll,
    cameraPermissionErrorModalState,
    imageCropperModalState,
    onCropComplete,
    croppingImage,
    handleRetryCropping,
    handleConfirmBadgePhoto,
    isUploadingBadgePhoto,
  } = useWorkBadgeSelectHeadshot({
    onConfirmBadgePhoto: onConfirm,
  });

  return (
    <>
      <StickyScrollPageHeader
        title="Select a Headshot for Your Badge"
        headerVariant="tertiary"
        onClickBack={onBack}
      />

      {isPicturesLoading && (
        <PageContent>
          <Stack height="100%" justifyContent="center" alignItems="center">
            <LoadingSpinner size="large" />
          </Stack>
        </PageContent>
      )}

      {!isPicturesLoading && (
        <>
          <PageContent>
            <Stack spacing={5}>
              <WorkBadgePreviewContainer
                qualificationName={qualificationName}
                workerHeadshotBase64={workerHeadshotBase64}
              />
            </Stack>
          </PageContent>
          <PageFooter>
            {isDefined(workerHeadshotBase64) ? (
              <Stack width="100%" spacing={3}>
                <LoadingButton
                  fullWidth
                  isLoading={isUploadingBadgePhoto}
                  size="large"
                  disabled={!workerHeadshotBase64}
                  onClick={handleConfirmBadgePhoto}
                >
                  Confirm badge photo
                </LoadingButton>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  disabled={isUploadingBadgePhoto}
                  onClick={badgePhotoSelectionModalState.openModal}
                >
                  Select new badge photo
                </Button>
              </Stack>
            ) : (
              <Button fullWidth size="large" onClick={badgePhotoSelectionModalState.openModal}>
                Choose badge photo
              </Button>
            )}
          </PageFooter>
        </>
      )}

      <BadgePhotoSelectionBottomSheet
        modalState={badgePhotoSelectionModalState}
        hasProfilePicture={hasProfilePicture}
        onTakePhoto={handleTakePhoto}
        onSelectFromCameraRoll={handleSelectPhotoFromCameraRoll}
        onUseProfilePicture={handleUseProfilePictureForBadge}
      />

      <CameraPermissionBottomSheet
        modalState={cameraPermissionErrorModalState}
        onCancel={() => {
          cameraPermissionErrorModalState.closeModal();
        }}
        onContinue={() => {
          cameraPermissionErrorModalState.closeModal();
        }}
      />

      {croppingImage && (
        <ImageCropperModal
          modalState={imageCropperModalState}
          imageSrc={croppingImage?.dataUrlBase64}
          onCropComplete={onCropComplete}
          onRetry={async () => {
            await handleRetryCropping();
          }}
        />
      )}
    </>
  );
}
